import React from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Link } from "wouter";

import Button, { ButtonTypes } from "../../../components/Button";
import { Skeleton } from "../../../components/Skeleton";
import { EmptyState } from "../../../components/States";
import { PendingTickerRequest } from "../../../services/tickerRequests";
import {
  InstrumentSelection,
  ManualPriceEntry,
} from "../components/InstrumentEditor";
import { SectionUnavailable } from "../components/SectionUnavailable";
import { JOBS_PATH } from "../paths";
import {
  Banner,
  BannerTitle,
  Caption,
  Card,
  PageContent,
  PageHeading,
  QueueLayout,
} from "../styles";
import { DecisionBanner } from "./components/DecisionBanner";
import { WorkCategoryTabs } from "./components/WorkCategoryTabs";
import { WorkItemDetail } from "./components/WorkItemDetail";
import { WorkItemList } from "./components/WorkItemList";
import { WorkQueueToolbar } from "./components/WorkQueueToolbar";
import { useCategoryLink } from "./hooks/useCategoryLink";
import { useWorkItemNavigation } from "./hooks/useWorkItemNavigation";
import { useWorkQueue } from "./hooks/useWorkQueue";
import {
  ActionError,
  BackgroundJobsLink,
  BackToQueue,
  DetailPane,
  QueueNavigation,
} from "./styles";
import { OpenActionItem, PendingFixItem } from "./types";
import { emptyCopyOf } from "./utils/filterWorkItems";

export const WorkQueuePage: React.FC = () => {
  useCategoryLink();
  const {
    view,
    items,
    counts,
    selected,
    outcome,
    nextItem,
    isLoading,
    isLoadFailed,
    isRefreshFailed,
    unloadedCategories,
    isSubmitting,
    isFixBlocked,
    isActionBlocked,
    actionError,
    fixErrors,
    detachedError,
    select,
    changeCategory,
    changeFilters,
    changeOrder,
    clearError,
    approve,
    reject,
    saveCountry,
    repair,
    applyManualPricing,
    deferAction,
    resumeAction,
    reconcileAction,
    retry,
  } = useWorkQueue();
  const {
    isStacked,
    detailRef,
    listRef,
    openDetail,
    backToList,
    handleDetailFocus,
  } = useWorkItemNavigation(outcome ? `outcome:${outcome.key}` : selected?.key);

  const hasControls = !isLoading && !isLoadFailed;
  const isEmpty = hasControls && selected === null;
  const emptyCopy = emptyCopyOf(view, unloadedCategories.length > 0);

  const handleSelect = (
    key: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    select(key);
    openDetail(event);
  };
  const handleApprove = (
    request: PendingTickerRequest,
    selection: InstrumentSelection,
  ) => void approve(request, selection);
  const handleReject = (request: PendingTickerRequest, reason: string) =>
    void reject(request, reason);
  const handleSaveCountry = (
    fix: PendingFixItem,
    sourceCountry: string | null,
  ) => void saveCountry(fix, sourceCountry);
  const handleRepairListing = (fix: PendingFixItem, yahooSymbol: string) =>
    void repair(fix, yahooSymbol);
  const handleUseManualPricing = (
    fix: PendingFixItem,
    entry: ManualPriceEntry,
  ) => void applyManualPricing(fix, entry);
  const handleDeferAction = (item: OpenActionItem) => void deferAction(item);
  const handleResumeAction = (item: OpenActionItem) => void resumeAction(item);
  const handleReconcileAction = (item: OpenActionItem) =>
    void reconcileAction(item);
  const handleNext = () => {
    if (nextItem) {
      select(nextItem.key);
    }
  };

  return (
    <PageContent>
      <PageHeading>Work queue</PageHeading>
      {isRefreshFailed && (
        <Banner $tone="warning" role="alert">
          <BannerTitle>Refresh failed</BannerTitle>
          <Caption>
            The queue below is from the last successful load. Try again in a
            moment.
          </Caption>
        </Banner>
      )}
      {unloadedCategories.length > 0 && (
        <Banner $tone="warning" role="alert">
          <BannerTitle>{`${unloadedCategories.join(" and ")} didn't load`}</BannerTitle>
          <Caption>The rest of the queue is still up to date.</Caption>
          <div>
            <Button type={ButtonTypes.link} onClick={retry}>
              Try again
            </Button>
          </div>
        </Banner>
      )}
      {detachedError && <ActionError role="alert">{detachedError}</ActionError>}
      <QueueNavigation>
        {hasControls && (
          <WorkCategoryTabs
            active={view.category}
            counts={counts}
            onChange={changeCategory}
          />
        )}
        <Link href={JOBS_PATH} asChild>
          <BackgroundJobsLink>Background jobs</BackgroundJobsLink>
        </Link>
      </QueueNavigation>
      {isLoading && <Skeleton variant="rectangular" height="12rem" />}
      {isLoadFailed && (
        <Card aria-label="Work queue">
          <SectionUnavailable observedAt={null} onRetry={retry} />
        </Card>
      )}
      {hasControls && (
        <WorkQueueToolbar
          view={view}
          itemCount={items.length}
          onFiltersChange={changeFilters}
          onOrderChange={changeOrder}
        />
      )}
      {isEmpty && (
        <EmptyState
          title={emptyCopy.title}
          description={emptyCopy.description}
        />
      )}
      {selected && (
        <QueueLayout>
          <WorkItemList
            items={items}
            selectedKey={selected.key}
            disabled={isSubmitting}
            listRef={listRef}
            onSelect={handleSelect}
          />
          <DetailPane
            ref={detailRef}
            tabIndex={-1}
            aria-label="Selected work item"
            onFocusCapture={handleDetailFocus}
          >
            {isStacked && (
              <BackToQueue>
                <Button type={ButtonTypes.link} onClick={backToList}>
                  <ArrowBackRoundedIcon fontSize="small" />
                  Back to queue
                </Button>
              </BackToQueue>
            )}
            {outcome && (
              <DecisionBanner
                resolution={outcome.resolution}
                hasNextItem={nextItem !== null}
                isSubmitting={isSubmitting}
                onNext={handleNext}
              />
            )}
            <WorkItemDetail
              item={selected}
              isSubmitting={isSubmitting}
              isFixBlocked={isFixBlocked}
              isActionBlocked={isActionBlocked}
              error={actionError}
              fixErrors={fixErrors}
              onApprove={handleApprove}
              onReject={handleReject}
              onSaveCountry={handleSaveCountry}
              onRepairListing={handleRepairListing}
              onUseManualPricing={handleUseManualPricing}
              onDeferAction={handleDeferAction}
              onResumeAction={handleResumeAction}
              onReconcileAction={handleReconcileAction}
              onClearError={clearError}
            />
          </DetailPane>
        </QueueLayout>
      )}
    </PageContent>
  );
};
